import { zodResolver } from '@hookform/resolvers/zod'
import { Info, Link2, Loader2, Unlink } from 'lucide-react'
import { Controller, useForm, useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { z } from 'zod'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import { Modal } from '@/components/ui/modal'
import { NumberInput } from '@/components/ui/number-input'
import { FormSelect } from '@/components/ui/select'
import { isApiError } from '@/lib/api/errors'
import { useModuleLinks } from '../../catalogApi'
import { MODULE_REQUIREMENTS, type CatalogModuleView, type ProjectSummary } from '../../types'
import { toastError } from '../../ui'
import { useUnlinkModule } from './useUnlinkModule'

/** create: modül ya da proje sabitlenebilir, diğeri seçilir. edit: mevcut bağlantı düzenlenir/çıkarılır. */
export type IntegrateTarget = { kind: 'create'; moduleId?: string; projectId?: string } | { kind: 'edit'; moduleId: string; projectId: string }

type Props = { target: IntegrateTarget | null; catalog: CatalogModuleView[]; projects: ProjectSummary[]; onClose: () => void }

export function IntegrateModal({ target, ...rest }: Props) {
  return target ? <IntegrateForm key={`${target.kind}-${target.moduleId ?? ''}-${target.projectId ?? ''}`} target={target} {...rest} /> : null
}

function IntegrateForm({ target, catalog, projects, onClose }: Props & { target: IntegrateTarget }) {
  const { t } = useTranslation()
  const { integrate, update } = useModuleLinks()
  const unlink = useUnlinkModule()
  const editing = target.kind === 'edit'
  const byId = new Map(catalog.map((m) => [m.id, m]))
  const linked = (moduleId: string, projectId: string) => !!byId.get(moduleId)?.usage.some((u) => u.projectId === projectId)
  const current = editing ? byId.get(target.moduleId)?.usage.find((u) => u.projectId === target.projectId) : undefined

  const schema = z.object({
    projectId: z.string().min(1, t('projects.catalog.integrate.projectRequired')),
    moduleId: z.string().min(1, t('projects.catalog.integrate.moduleRequired')),
    requirement: z.enum(MODULE_REQUIREMENTS),
    price: z.string().regex(/^\d+(\.\d{1,2})?$/, t('projects.catalog.form.priceInvalid')),
  })
  const initialModule = target.moduleId ? byId.get(target.moduleId) : undefined
  const form = useForm({
    resolver: zodResolver(schema),
    defaultValues: {
      projectId: target.projectId ?? '',
      moduleId: target.moduleId ?? '',
      requirement: current?.requirement ?? 'optional',
      price: current?.price ?? initialModule?.price ?? '',
    },
  })
  const { errors } = form.formState
  const [projectId, moduleId] = useWatch({ control: form.control, name: ['projectId', 'moduleId'] })
  const module = byId.get(moduleId)
  const project = projects.find((p) => p.id === projectId)
  const missing = module && projectId && !editing ? module.dependsOn.filter((d) => !linked(d, projectId)).map((d) => byId.get(d)?.name ?? d) : []

  const projectOptions = projects.map((p) => ({ value: p.id, label: p.name, disabled: !editing && !!moduleId && linked(moduleId, p.id) }))
  const moduleOptions = catalog.map((m) => ({
    value: m.id,
    label: m.name,
    description: m.code,
    keywords: [m.code, m.name],
    disabled: !editing && (!m.active || (!!projectId && linked(m.id, projectId))),
  }))

  const onSubmit = form.handleSubmit(async (v) => {
    const input = { requirement: v.requirement, price: v.price }
    try {
      if (editing) {
        await update.mutateAsync({ projectId: v.projectId, moduleId: v.moduleId, input })
        toast.success(t('projects.catalog.integrate.updated'))
      } else {
        const added = await integrate.mutateAsync({ projectId: v.projectId, moduleId: v.moduleId, input })
        toast.success(t('projects.catalog.integrate.done', { module: module?.name ?? '', project: project?.name ?? '' }), {
          description: added.length ? t('projects.catalog.integrate.depsAdded', { names: added.map((id) => byId.get(id)?.name ?? id).join(', ') }) : undefined,
        })
      }
      onClose()
    } catch (e) {
      if (isApiError(e) && e.code === 'MODULE_ALREADY_LINKED') form.setError('moduleId', { message: t('errors.MODULE_ALREADY_LINKED') })
      else toastError(e)
    }
  })

  const onUnlink = async () => {
    if (await unlink({ moduleId: target.moduleId as string, moduleName: module?.name ?? '', projectId: target.projectId as string, projectName: project?.name ?? '' })) onClose()
  }
  const pending = integrate.isPending || update.isPending

  return (
    <Modal
      open
      onOpenChange={(v) => !v && onClose()}
      dismissible={!pending}
      icon={<Link2 />}
      title={t(editing ? 'projects.catalog.integrate.editTitle' : 'projects.catalog.integrate.title')}
      description={editing ? t('projects.catalog.integrate.editHint', { module: module?.name ?? '', project: project?.name ?? '' }) : t('projects.catalog.integrate.hint')}
      onSubmit={(e) => void onSubmit(e)}
      footer={
        <>
          {editing ? (
            <Button type="button" variant="outline" className="text-destructive hover:text-destructive sm:mr-auto" onClick={() => void onUnlink()}>
              <Unlink />
              {t('projects.modules.remove')}
            </Button>
          ) : null}
          <Button type="button" variant="cancel" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button type="submit" disabled={pending}>
            {pending ? <Loader2 className="animate-spin" /> : null}
            {t(editing ? 'common.save' : 'projects.catalog.integrate.submit')}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor="ig-module">{t('projects.catalog.integrate.module')}</Label>
          <FormSelect
            id="ig-module"
            control={form.control}
            name="moduleId"
            disabled={editing || !!target.moduleId}
            placeholder={t('common.select')}
            options={moduleOptions}
            onValueChange={(id) => {
              const m = byId.get(id)
              if (m) form.setValue('price', m.price)
            }}
          />
          {errors.moduleId ? <p className="text-xs text-destructive">{errors.moduleId.message}</p> : null}
        </div>
        <div className="space-y-2">
          <Label htmlFor="ig-project">{t('projects.catalog.project')}</Label>
          <FormSelect id="ig-project" control={form.control} name="projectId" disabled={editing || !!target.projectId} placeholder={t('common.select')} options={projectOptions} />
          {errors.projectId ? <p className="text-xs text-destructive">{errors.projectId.message}</p> : null}
        </div>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div className="space-y-2">
            <Label htmlFor="ig-req">{t('projects.modules.columns.requirement')}</Label>
            <FormSelect id="ig-req" control={form.control} name="requirement" options={MODULE_REQUIREMENTS.map((r) => ({ value: r, label: t(`projects.requirement.${r}`), description: t(r === 'required' ? 'projects.inclusion.includedHint' : 'projects.inclusion.addableHint') }))} />
          </div>
          <div className="space-y-2">
            <Label htmlFor="ig-price">{t('projects.catalog.fields.projectPrice')}</Label>
            <Controller
              control={form.control}
              name="price"
              render={({ field }) => <NumberInput id="ig-price" value={field.value} onChange={field.onChange} onBlur={field.onBlur} minDecimalsOnBlur={2} suffix="₺" aria-invalid={!!errors.price} />}
            />
            {errors.price ? <p className="text-xs text-destructive">{errors.price.message}</p> : null}
          </div>
        </div>
        {missing.length ? (
          <p className="flex items-start gap-2 rounded-lg border border-info/30 bg-info/[0.06] p-3 text-xs">
            <Info className="mt-0.5 size-4 shrink-0 text-info" aria-hidden />
            {t('projects.catalog.integrate.depsNote', { names: missing.join(', ') })}
          </p>
        ) : null}
      </div>
    </Modal>
  )
}
